/**
 * @enum {string}
 */
const IFListenerMessageType = Object.freeze({
  RUN_RESULT: "run_result",
  BRIDGE_MESSAGE: "bridge_message",
});

/**
 * @enum {string}
 */
const IFPostMessageType = Object.freeze({
  ECHO: "echo",
  RUN: "run",
});

/**
 * @typedef IFListenerBridgeMessage
 * @property {IFListenerMessageType} type
 * @property {string} bridgeName
 * @property {any[]} args
 */

/**
 * @typedef IFPostRunMessage
 * @property {string} id
 * @property {IFPostMessageType} type
 * @property {string} methodName
 * @property {any[]} args
 */

/**
 * @typedef IFListenerRunResultSuccessMessage
 * @property {string} id
 * @property {IFListenerMessageType} type
 * @property {any} result
 */

/**
 * @typedef IFListenerRunResultErrorMessage
 * @property {string} id
 * @property {IFListenerMessageType} type
 * @property {string} error
 */

// NOTE: Might want to specify for cross-site security in the future. Current bridges don't require it.
const HYBRIDAPI_ORIGIN = "*";
const HYBRIDAPI_VALID_ARG_TYPES = new Set([
  "undefined",
  "boolean",
  "number",
  "bigint",
  "string",
]);

function hybridapi(methodName, ...methodArguments) {
  if (window["hybridapi"] && window["hybridapi"][methodName]) {
    try {
      window["hybridapi"][methodName](
        ...methodArguments.map((arg) => {
          if (typeof arg == "object") {
            return JSON.stringify(arg);
          } else if (typeof arg == "number") {
            return "" + arg;
          }
          return arg;
        })
      );
    } catch (error) {
      console.error("hybridapi android error method name:", methodName);
      console.error(
        "hybridapi android error method arguments:",
        methodArguments
      );
      console.error("hybridapi android error", error);
    }
  } else if (
    window["webkit"] &&
    window["webkit"]["messageHandlers"] &&
    window["webkit"]["messageHandlers"][methodName]
  ) {
    try {
      window["webkit"]["messageHandlers"][methodName]["postMessage"](
        methodArguments
      );
    } catch (error) {
      console.error("hybridapi iOS error", error);
    }
  } else if (window !== window.top) {
    /** @type {IFListenerBridgeMessage} */
    const message = {
      type: IFListenerMessageType.BRIDGE_MESSAGE,
      bridgeName: methodName,
      args: methodArguments,
    };
    window.top.postMessage(message, HYBRIDAPI_ORIGIN);
  } else {
    // NOTE(baris): This log is used for testing. Hybrid methods should not be called in production if running as website.
    // console.error("$hybridapi:NONE " + methodName + " " + methodArguments);
  }
}

window.addEventListener("message", function (event) {
  //Might want to check for cross-site security in the future. Current methods set to `window` don't require it.

  if (event.data.type === IFPostMessageType.ECHO) {
    window.parent.postMessage(event.data.data, event.origin);
  } else if (event.data.type === IFPostMessageType.RUN) {
    /** @type {IFPostRunMessage} */
    const { id, methodName, args } = event.data;

    if (methodName === "eval") {
      throw "unexpected eval";
    } else if (methodName === "fetch") {
      throw "unexpected fetch";
    } else if (methodName === "open") {
      throw "unexpected open";
    } else if (methodName === "prompt") {
      throw "unexpected prompt";
    }

    if (!(methodName in window) || typeof window[methodName] !== "function") {
      throw "unexpected methodName: " + methodName;
    }

    const invalidArgs = args.filter(
      (arg) => arg !== null && !HYBRIDAPI_VALID_ARG_TYPES.has(typeof arg)
    );
    if (invalidArgs.length > 0) {
      console.error("$hybridapi:web <incoming:run> invalid args", invalidArgs);
      throw "invalid arg";
    }

    let result = null;
    try {
      result = window[methodName](...args);
    } catch (error) {
      console.error('failed "run" event', error);

      /** @type {IFListenerRunResultErrorMessage} */
      const resultErrorMessage = {
        id,
        type: IFListenerMessageType.RUN_RESULT,
        error: "" + error,
      };
      event.source.postMessage(resultErrorMessage, event.origin);
      return;
    }

    // To make sure 'result' field is always sent.
    if (result === undefined) {
      result = null;
    }

    /** @type {IFListenerRunResultSuccessMessage} */
    const resultSuccessMessage = {
      id,
      type: IFListenerMessageType.RUN_RESULT,
      result,
    };
    event.source.postMessage(resultSuccessMessage, event.origin);
  }
});

module.exports = hybridapi;
